import { useEffect, useRef, useState } from "react";
import { X, FileText, PenLine, Eraser, CheckCircle2, ExternalLink } from "lucide-react";
import { http, errMsg, fmtDateTime } from "../api";

export function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const pos = e => {
    const r = ref.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: (p.clientX - r.left) * (ref.current.width / r.width), y: (p.clientY - r.top) * (ref.current.height / r.height) };
  };
  const start = e => { e.preventDefault(); drawing.current = true; const c = ref.current.getContext("2d"); const { x, y } = pos(e); c.beginPath(); c.moveTo(x, y); };
  const move = e => {
    if (!drawing.current) return; e.preventDefault();
    const c = ref.current.getContext("2d"); const { x, y } = pos(e);
    c.lineWidth = 3; c.lineCap = "round"; c.lineJoin = "round"; c.strokeStyle = "#0f172a"; c.lineTo(x, y); c.stroke();
    if (!dirty.current) { dirty.current = true; }
    onChange(ref.current.toDataURL("image/png"));
  };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = ref.current.getContext("2d"); c.clearRect(0, 0, ref.current.width, ref.current.height); dirty.current = false; onChange(""); };
  return (
    <div>
      <canvas ref={ref} width={600} height={180} className="sig-pad" data-testid="signature-pad"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
        <small style={{ color: "var(--muted)" }}><PenLine size={12} style={{ display: "inline" }} /> Sign above with mouse or finger</small>
        <button type="button" className="ghost" onClick={clear} data-testid="signature-clear"><Eraser size={13} /> Clear</button>
      </div>
    </div>
  );
}

export default function AgreementSignModal({ order, onClose, onAllSigned }) {
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);
  const [sig, setSig] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = () => http.get(`/orders/${order.id}/agreements`).then(r => { setItems(r.data); const next = r.data.find(a => !a.signed); setActive(next || null); setSig(""); setAccepted(false); });
  useEffect(() => { load(); }, [order.id]);

  const openPdf = async id => {
    const { data } = await http.get(`/files/${id}`, { responseType: "blob" });
    window.open(URL.createObjectURL(data), "_blank");
  };
  const sign = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await http.post(`/orders/${order.id}/agreements/${active.id}/sign`, { signature: sig, accepted });
      if (data.remaining === 0) { onAllSigned?.(); }
      await load();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  if (!items) return null;
  const pending = items.filter(a => !a.signed).length;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} data-testid="agreement-modal">
        <div className="modal-head">
          <div><h2>Agreements for {order.order_code}</h2><p style={{ fontSize: 13, marginTop: 4 }}>{order.client_company || order.client_name} requires {items.length} agreement(s). {pending ? `${pending} pending.` : "All signed."}</p></div>
          <button className="modal-close" onClick={onClose} data-testid="agreement-close"><X size={18} /></button>
        </div>
        <div className="list-rows" style={{ marginBottom: 14 }}>
          {items.map(a => (
            <div key={a.id} className={`list-row ${active?.id === a.id ? "active-border" : ""}`} data-testid={`agreement-item-${a.id}`}>
              <FileText size={18} color={a.signed ? "var(--success)" : "var(--primary)"} />
              <div className="list-row-info"><strong>{a.title}</strong><p>{a.pages} page(s){a.description ? ` · ${a.description}` : ""}{a.signed ? ` · Signed ${fmtDateTime(a.signed_at)}` : ""}</p></div>
              <div className="list-row-actions">
                <button className="ghost" onClick={() => openPdf(a.signed ? a.signed_id : a.id)} data-testid={`agreement-open-${a.id}`}><ExternalLink size={13} /> {a.signed ? "Signed copy" : "Read PDF"}</button>
                {a.signed ? <span className="badge success"><CheckCircle2 size={12} /> Signed</span> : <button className="pill-action neutral" onClick={() => { setActive(a); setSig(""); setAccepted(false); }} data-testid={`agreement-select-${a.id}`}>Sign</button>}
              </div>
            </div>
          ))}
        </div>
        {active && (
          <div className="card" data-testid="agreement-sign-area">
            <div className="card-head"><h2>Sign: {active.title}</h2></div>
            <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 10 }}>Read the PDF first. Your signature, name, date, time and IP will be stamped on the footer of every page.</p>
            <SignaturePad onChange={setSig} />
            <label className={`checkbox-label ${accepted ? "checked" : ""}`} style={{ marginTop: 12, display: "inline-flex" }} data-testid="agreement-accept">
              <input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)} /> I have read and agree to the terms of "{active.title}"
            </label>
            {err && <div className="notice error" style={{ marginTop: 10 }}>{err}</div>}
            <button className="primary" style={{ marginTop: 12 }} disabled={busy || !sig || !accepted} onClick={sign} data-testid="agreement-submit">{busy ? "Stamping signature…" : "Sign & continue"}</button>
          </div>
        )}
        {!active && pending === 0 && <div className="notice success"><CheckCircle2 size={15} /> All agreements signed. You can now accept the order.</div>}
      </div>
    </div>
  );
}
